import { z } from 'zod';
import { ConnectorErrorEntry, ConnectorStatus } from './status.js';
import { ConnectorInstallScope, ConnectorKind } from './kind.js';
import { ConnectorSecrets } from './secrets.js';
export const ORG_CONNECTORS_COLLECTION = 'orgConnectors';
export const ORG_CONNECTOR_SECRETS_COLLECTION = 'orgConnectorSecrets';
/**
 * Compose the Firestore doc id for a connector row. Single underscore
 * between segments; an optional `memberUid` segment is appended for
 * per-user installs (e.g. Gmail). Both the public `orgConnectors` and
 * the sibling `orgConnectorSecrets` collections share this id.
 *
 * Single source of truth — both server-side store callers (functions)
 * and client-side verifications (e2e helpers, UI sample reads) should
 * import this helper rather than re-concatenating the parts.
 */
export function connectorDocId(args) {
    // Validate kind through the enum so callers can't pass a typo.
    // `ConnectorKind` is both a type and a Zod enum runtime value.
    const kind = ConnectorKind.parse(args.kind);
    if (args.memberUid) {
        return `${args.orgId}_${kind}_${args.memberUid}`;
    }
    return `${args.orgId}_${kind}`;
}
/**
 * Public connector state. One doc per (orgId, kind) pair — the doc id
 * is `${orgId}_${kind}` for workspace scope and
 * `${orgId}_${kind}_${memberUid}` for per-user (Gmail). NO SECRETS on
 * this doc — those live in the sibling `orgConnectorSecrets/{id}`
 * collection (same id; separate rules; server-only).
 *
 * Reads gated by rules' `isOrgAdmin(orgId)`; writes are server-only.
 * Direct Firestore SDK reads return the same shape the API serves;
 * there's no separate sanitization layer needed because the schema
 * itself carries no tokens.
 */
export const OrgConnector = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    kind: ConnectorKind,
    installScope: ConnectorInstallScope,
    /** Set when scope === 'per-user'. */
    memberUid: z.string().optional(),
    status: ConnectorStatus,
    /** ISO timestamp of the last successful ingest (any path). */
    lastSyncAt: z.string().nullable().default(null),
    /** ISO timestamp when the backfill completed; null while backfilling. */
    backfillCompletedAt: z.string().nullable().default(null),
    /** Last ~20 error entries — older entries are evicted on write. */
    errorLog: z.array(ConnectorErrorEntry).default([]),
    /** Per-connector scope configuration (e.g. Slack channel ids, Salesforce sObjects). */
    scope: z.record(z.string(), z.unknown()).default({}),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Per-install secret bag. Stored in `orgConnectorSecrets/{id}` where
 * `id` matches the public connector's doc id. Server-only — rules deny
 * all client reads + writes. The store layer is the only consumer.
 *
 * Split from `OrgConnector` so a curious orgAdmin can't read the
 * tokens via the Firestore SDK even though the public connector doc
 * is admin-readable. Reduces blast radius.
 */
export const OrgConnectorSecret = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    kind: ConnectorKind,
    secrets: ConnectorSecrets,
    updatedAt: z.string(),
});
/**
 * Backwards-compatible alias — the public connector IS the client-
 * facing shape. Kept under the old `PublicOrgConnector` name so
 * existing API + UI imports don't churn.
 */
export const PublicOrgConnector = OrgConnector;
/**
 * Body for `POST /api/orgs/:o/connectors/:kind/scope`. Used by the
 * admin UI after OAuth completes to pick which subset of data to
 * ingest (e.g. Slack channel selection, Salesforce sObjects).
 */
export const UpdateConnectorScopeRequest = z.object({
    scope: z.record(z.string(), z.unknown()),
});
